import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Zap } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const LINKS = [
  { to: "/how-it-works", label: "How It Works" },
  { to: "/products", label: "Products" },
  { to: "/plans", label: "Plans" },
  { to: "/business", label: "Business" },
  { to: "/technology", label: "Technology" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,padding] duration-300 ${
        scrolled ? "border-b border-white/10 bg-navy/70 py-3 backdrop-blur-xl" : "border-b border-transparent py-5"
      }`}
      data-testid="navbar"
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 md:px-8">
        <Link to="/" className="group flex items-center gap-2" data-testid="logo-home-link">
          <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-revo-green">
            <Zap className="h-4 w-4 text-navy" strokeWidth={2.5} fill="currentColor" />
          </span>
          <span className="font-display text-xl font-extrabold tracking-tight text-white">ChargeRevo</span>
        </Link>

        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/", "")}`}
              className={`text-sm font-medium transition-colors duration-200 hover:text-white ${
                pathname === l.to ? "text-revo-green" : "text-revo-ice"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/join"
            data-testid="nav-join-waitlist-btn"
            className="hidden rounded-full bg-revo-green px-5 py-2.5 text-sm font-bold text-navy transition-[transform,box-shadow] duration-200 hover:scale-[1.03] hover:shadow-[0_0_30px_rgba(47,213,118,0.5)] sm:inline-block"
          >
            Join the waitlist
          </Link>
          <button
            className="text-white lg:hidden"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-t border-white/10 bg-navy/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => (
                <Link key={l.to} to={l.to} className="py-3 text-base font-medium text-revo-ice" data-testid={`mobile-nav-${l.to.replace("/", "")}`}>
                  {l.label}
                </Link>
              ))}
              <Link to="/join" className="mt-2 rounded-full bg-revo-green px-5 py-3 text-center font-bold text-navy" data-testid="mobile-join-btn">
                Join the waitlist
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
